import { useParams, Link } from 'react-router-dom';
import {
  Badge,
  Card,
  CardHeader,
  ProviderLogo,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui';
import { useIntegrationSubscription, useSubscriptionLiveUpdate } from '../../hooks/useIntegrationSubscriptions';
import {
  useProviders,
  useIntegrationEvents,
  useIntegrationEventDetail,
} from '../../hooks/useIntegrations';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { getProviderFallback } from '../../lib/provider-instructions';
import { formatRelativeTime } from '../activity/utils';
import IntegrationDeliveriesCard from './IntegrationDeliveriesCard';
import BackLink from '../../components/navigation/BackLink';

export default function SubscriptionDetail() {
  const { integrationId } = useParams<{ integrationId: string }>();
  const spaceId = useSpaceId();
  useSubscriptionLiveUpdate(spaceId, integrationId);
  const { data: subscription, isLoading, error } = useIntegrationSubscription(
    spaceId,
    integrationId,
  );
  const { data: providers } = useProviders();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !subscription || !integrationId) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Subscription not found</p>
        <Link
          to={routes.console.spaceIntegrations(spaceId)}
          className="text-primary-600 hover:text-primary-500 mt-2 inline-block"
        >
          Back to integrations
        </Link>
      </div>
    );
  }

  const providerInfo = providers?.find((p) => p.name === subscription.provider);
  const providerDisplayName =
    providerInfo?.displayName ?? getProviderFallback(subscription.provider).displayName;

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.spaceIntegrations(spaceId)}>Integrations</BackLink>

      {/* Header */}
      <div className="flex items-center gap-4">
        <ProviderLogo
          slug={subscription.provider}
          displayName={providerDisplayName}
          className="h-12 w-12"
          fallbackClassName="text-sm"
        />
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">
              {subscription.displayName}
            </h1>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="info" className="cursor-default">From org</Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                Managed by org admins
              </TooltipContent>
            </Tooltip>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {providerDisplayName} subscription
          </p>
        </div>
      </div>

      {/* Overview */}
      <Card>
        <CardHeader title="Overview" />
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Routed events</dt>
            <dd className="mt-1 text-2xl font-semibold text-foreground">
              {subscription.eventCount ?? 0}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Event types</dt>
            <dd className="mt-1 text-2xl font-semibold text-foreground">
              {subscription.eventTypeCount}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Rules</dt>
            <dd className="mt-1 text-2xl font-semibold text-foreground">
              {subscription.ruleCount}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Last event</dt>
            <dd className="mt-1 text-sm text-foreground">
              {subscription.lastEventAt ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-default">
                      {formatRelativeTime(subscription.lastEventAt)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">
                    {new Date(subscription.lastEventAt).toLocaleString()}
                  </TooltipContent>
                </Tooltip>
              ) : (
                'Never'
              )}
            </dd>
          </div>
        </dl>
      </Card>

      {/* Event types — flat summary of every event type that lands in this
          space from this integration, across all rules. */}
      <Card>
        <CardHeader
          title="Event types"
          description="Events from this integration that are routed into this space."
        />
        {subscription.eventTypes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No event types routed yet.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {subscription.eventTypes.map((et) => (
              <span
                key={et}
                className="font-mono text-xs bg-muted px-2 py-1 rounded"
              >
                {et}
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Subscribed rules */}
      <Card>
        <CardHeader
          title="Subscribed rules"
          description="Routing rules the org admin has configured that forward events from this integration into this space."
        />
        {subscription.rules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No active rules.</p>
        ) : (
          <ul className="divide-y">
            {subscription.rules.map((rule) => (
              <li key={rule.ruleId} className="py-3 space-y-1.5">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium text-foreground">
                    {rule.description ?? rule.ruleId}
                  </span>
                  {rule.pinned ? (
                    <Badge variant="info" className="text-xs">pinned</Badge>
                  ) : (
                    <Badge variant="gray" className="text-xs">all event types</Badge>
                  )}
                </div>
                {rule.eventTypes.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {rule.eventTypes.map((et) => (
                      <span
                        key={et}
                        className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded"
                      >
                        {et}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Deliveries — shares the same cache prefix as the space-owned
          integration detail view because both surfaces hit
          `/conn/integrations/{id}/events`; the backend slices to events
          visible to the caller's space when the integration is org-owned. */}
      <IntegrationDeliveriesCard
        integrationId={integrationId}
        useEvents={useIntegrationEvents}
        useEventDetail={useIntegrationEventDetail}
        queryKeyPrefix={['space', spaceId, 'integrations', integrationId]}
        title="Routed Deliveries"
        description="Events from this org integration that matched a rule for this space."
      />
    </div>
  );
}
